import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, zPhoneNumber } from '@harvestiq/zod';
import { Button } from '@harvestiq/ui/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@harvestiq/ui/components/ui/form';
import { Input } from '@harvestiq/ui/components/ui/input';
import {
  useAppSettings,
  useModal,
  UserFromContext,
  useToast,
  useUser,
} from '../../contexts';
import { useUpdateUser, useSmsOptInConfirmation } from '../../queries/users';
import { InputMask, format } from '@react-input/mask';
import { SmsOptInCheckbox } from '@harvestiq/ui/components/SmsOptInCheckbox';
import { pages } from 'common';
import { useFeatureFlags, FeatureFlags } from '../../hooks/useFeatureFlag';

const phoneMaskOptions = {
  mask: '___-___-____',
  replacement: { _: /\d/ },
};

const ssnMaskOptions = {
  mask: '___-__-____',
  replacement: { _: /\d/ },
};

const createCompleteProfileSchema = (isAgent: boolean, smsEnabled: boolean) =>
  z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    phoneNumber: zPhoneNumber({
      stringRequiredError: 'Mobile phone number is required',
      regexMessage: 'Please enter a valid mobile phone number',
    }).transform((val) => val.replaceAll('-', '')),
    ssn: isAgent
      ? z
          .string()
          .min(1, 'SSN is required for agents')
          .transform((val) => val.replaceAll('-', ''))
      : z
          .string()
          .optional()
          .transform((val) => (val ? val.replaceAll('-', '') : undefined)),
    smsOptIn: smsEnabled ? z.boolean() : z.boolean().optional(),
  });

type CompleteProfileModalProps = {
  user: UserFromContext;
};

const CompleteProfileModal = ({ user }: CompleteProfileModalProps) => {
  const { appSettings } = useAppSettings();
  const { isUserProfileComplete, getMe } = useUser();
  const { closeModal } = useModal();
  const { errorToast, successToast } = useToast();
  const updateUserMutation = useUpdateUser();
  const { isCurrentlyOptedIn, sendOptInOrOptOutConfirmationSms } =
    useSmsOptInConfirmation();
  const { featureEnabled: smsEnabled } = useFeatureFlags(
    FeatureFlags.ENABLE_SMS_CAMPAIGNS
  );

  const completeProfileSchema = createCompleteProfileSchema(
    user.isAgent,
    smsEnabled
  );

  type FormValues = z.infer<typeof completeProfileSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.mobilePhoneNumber
        ? format(user.mobilePhoneNumber, phoneMaskOptions)
        : '',
      ssn: user?.ssn ? format(user.ssn, ssnMaskOptions) : '',
      smsOptIn: isCurrentlyOptedIn(user),
    },
  });

  useEffect(() => {
    if (isUserProfileComplete) {
      closeModal();
    }
  }, [isUserProfileComplete, closeModal]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await updateUserMutation.mutateAsync(
        {
          userId: user.id,
          body: {
            firstName: values.firstName,
            lastName: values.lastName,
            ssn: values.ssn,
            mobilePhoneNumber: values.phoneNumber,
            ...(smsEnabled && { smsOptIn: values.smsOptIn }),
          },
        },
        {
          onSuccess: async () => {
            if (smsEnabled && values.smsOptIn !== undefined) {
              await sendOptInOrOptOutConfirmationSms({
                optIn: values.smsOptIn,
                user,
              });
            } else {
              await getMe();
            }
            successToast('Profile successfully updated!');
          },
          onError: () => {
            errorToast('Oops, something went wrong. Please try again.');
          },
        }
      );
    } catch (error) {
      console.error('Error updating profile:', error);
      errorToast('Oops, something went wrong. Please try again.');
    }
  };

  return (
    <div className="modal p1 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">
          Complete your profile to get started
        </h2>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile Phone Number</FormLabel>
                <FormControl>
                  <InputMask
                    {...field}
                    {...phoneMaskOptions}
                    showMask
                    placeholder="Enter your mobile phone number"
                    component={Input}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ssn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SSN</FormLabel>
                <FormControl>
                  <InputMask
                    {...field}
                    {...ssnMaskOptions}
                    showMask
                    placeholder="Enter your SSN"
                    component={Input}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {smsEnabled && (
            <SmsOptInCheckbox
              control={form.control}
              name="smsOptIn"
              orgName={appSettings.name}
              tosLink={pages.termsAndConditions.path}
              privacyPolicyLink={pages.privacyPolicy.path}
            />
          )}

          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              disabled={updateUserMutation.isPending}
              data-cy="user-profile-form-save-btn"
            >
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CompleteProfileModal;
